<script lang="ts">
  import { untrack } from 'svelte';
  import { CaretDown } from 'phosphor-svelte';

  import { css } from '@agapi/ui/panda/css';

  import { Button } from '../button';
  import { expandableStyles } from './expandable.styles';
  import type { ExpandableProps } from './expandable.types';
  import { onMount, tick } from 'svelte';

  const id = $props.id();

  // References
  let rootRef = $state<HTMLElement | undefined>();
  let contentRef = $state<HTMLElement | undefined>();

  // States
  let isExpanded = $state(false);
  let hasMoreContent = $state(false);

  const { showLabel, hideLabel, buttonVariant, buttonSize = 'xs', fade = true, hideIcon = false, initialHeight = 200, children }: ExpandableProps = $props();

  onMount(() => {
    const parseStyleValue = (value: string) => parseFloat(value.replace('px', ''));

    const resizeObserver = new ResizeObserver(([{ target }]) => {
      // Compare target's padding with children's max padding
      const padding = Math.max(
        ...Array.from(target.children).map(c => parseStyleValue(getComputedStyle(c as HTMLElement).padding)),
        parseStyleValue(getComputedStyle(target as HTMLElement).padding)
      );
      // Update if content exceeds initial height accounting for padding
      hasMoreContent = (target.scrollHeight - padding) > initialHeight;
    });

    if (contentRef) {
      resizeObserver.observe(contentRef);
    }

    return () => resizeObserver.disconnect();
  });

  $effect.pre(() => {
    if (rootRef) {
      rootRef.style.height = hasMoreContent ? isExpanded ? `${rootRef.scrollHeight}px` : `${initialHeight}px` : 'auto';
    }
  });

  onMount(() => {
    if (rootRef) {
      hasMoreContent = rootRef.scrollHeight > initialHeight;
      tick().then(() => {
        rootRef!.style.maxHeight = 'unset';
      });
    }
  });

  const classes = expandableStyles();
</script>

<div class={classes.root}>
  <div
    id={id}
    class={classes.motionBox}
    style="display: block; max-height: {initialHeight}px;"
    style:mask-image={fade && hasMoreContent ? undefined : 'none'}
    bind:this={rootRef}
  >
    <div bind:this={contentRef}>
      {@render children?.()}
    </div>

    {#if hasMoreContent}
      <div class={css({ paddingBottom: 'calc(token(spacing.6) + token(spacing.2))' })}></div>
    {/if}
  </div>

  {#if hasMoreContent}
    <div class={classes.stack}>
      <Button variant={buttonVariant} size={buttonSize} onclick={() => (isExpanded = !isExpanded)} aria-expanded={isExpanded} aria-controls={id} css={{ pointerEvents: 'all' }}>
        {isExpanded ? hideLabel : showLabel}

        {#if !hideIcon}
          <CaretDown class={classes.rotateIcon} style={isExpanded ? 'transform: rotate(180deg)' : ''} />
        {/if}
      </Button>
    </div>
  {/if}
</div>
